import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AuthGateProvider } from "./context/AuthGateContext";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Core pages
import HomePage from "./components/HomePage";
import PropertyDetails from "./components/PropertyDetails";
import LoginPage from "./components/auth/LoginPage";
import RegisterPage from "./components/auth/RegisterPage";
import AuthCallback from "./components/auth/AuthCallback";
import ProfileSetup from "./components/auth/ProfileSetup";
import StudentDashboard from "./components/dashboards/StudentDashboard";
import LandlordDashboard from "./components/dashboards/LandlordDashboard";

// Static pages — Company
import AboutUs from "./components/pages/AboutUs";
import HowItWorks from "./components/pages/HowItWorks";
import Careers from "./components/pages/Careers";
import Press from "./components/pages/Press";
import PartnerWithUs from "./components/pages/PartnerWithUs";

// Static pages — For Students
import StudentGuide from "./components/pages/StudentGuide";
import CityGuides from "./components/pages/CityGuides";
import UniversityPartnerships from "./components/pages/UniversityPartnerships";
import StudentResources from "./components/pages/StudentResources";

// Static pages — For Landlords
import ListYourProperty from "./components/pages/ListYourProperty";
import PropertyManagement from "./components/pages/PropertyManagement";
import LandlordResources from "./components/pages/LandlordResources";
import Pricing from "./components/pages/Pricing";
import SuccessStories from "./components/pages/SuccessStories";

// Static pages — Support
import ContactUs from "./components/pages/ContactUs";
import HelpCenter from "./components/pages/HelpCenter";
import SafetyPage from "./components/pages/SafetyPage";
import TrustSafety from "./components/pages/TrustSafety";
import CommunityGuidelines from "./components/pages/CommunityGuidelines";

// Static pages — Legal / Bottom bar
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import TermsOfService from "./components/pages/TermsOfService";
import CookiePolicy from "./components/pages/CookiePolicy";
import Accessibility from "./components/pages/Accessibility";
import SearchResults from "./components/pages/SearchResults";
import SubmitListing from "./pages/SubmitListing";
import InspectionConfirmed from "./components/pages/InspectionConfirmed";
import SubscriptionSuccess from "./components/pages/SubscriptionSuccess";
import VibemateLanding from "./pages/VibemateLanding";
import VibemateApp from "./pages/VibemateApp";
import AdminPanel from "./components/pages/AdminPanel";
import FoundingWaitlist from "./pages/FoundingWaitlist";
import RewardsExplainer from "./pages/RewardsExplainer";
import OnboardingRenter from "./pages/OnboardingRenter";
import OnboardingLandlord from "./pages/OnboardingLandlord";
import PrivateListingView from "./pages/PrivateListingView";
import LandlordLoginPage from "./components/auth/LandlordLoginPage";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";

// Referral redirect: /join?ref=XXXX → /register/student?ref=XXXX
function JoinRedirect() {
  const { search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/register/student${search}`, { replace: true });
  }, [navigate, search]);
  return null;
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <AuthProvider>
      <BrowserRouter>
      <AuthGateProvider>
      <div className="App">
          <Routes>
            {/* Core */}
            <Route path="/" element={<HomePage />} />
            <Route path="/property/:id" element={<PropertyDetails />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register/:userType" element={<RegisterPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/profile-setup" element={<ProfileSetup />} />
            <Route path="/student-dashboard" element={<StudentDashboard />} />
            <Route path="/landlord-dashboard" element={<LandlordDashboard />} />

            {/* Company */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/press" element={<Press />} />
            <Route path="/partner" element={<PartnerWithUs />} />

            {/* For Students */}
            <Route path="/student-guide" element={<StudentGuide />} />
            <Route path="/city-guides" element={<CityGuides />} />
            <Route path="/universities" element={<UniversityPartnerships />} />
            <Route path="/resources" element={<StudentResources />} />

            {/* For Landlords */}
            <Route path="/list-property" element={<ListYourProperty />} />
            <Route path="/property-management" element={<PropertyManagement />} />
            <Route path="/landlord-resources" element={<LandlordResources />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/success-stories" element={<SuccessStories />} />

            {/* Support */}
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/trust-safety" element={<TrustSafety />} />
            <Route path="/guidelines" element={<CommunityGuidelines />} />

            {/* Legal */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/search-results" element={<SearchResults />} />
            <Route path="/submit-listing" element={<SubmitListing />} />
            <Route path="/inspection-confirmed" element={<InspectionConfirmed />} />
            <Route path="/subscription-success" element={<SubscriptionSuccess />} />
            <Route path="/vibemate" element={<VibemateLanding />} />
            <Route path="/vibemate/app" element={<VibemateApp />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/founding" element={<FoundingWaitlist />} />
            <Route path="/waitlist" element={<FoundingWaitlist />} />
            <Route path="/how-rewards-work" element={<RewardsExplainer />} />
            <Route path="/landlord/login" element={<LandlordLoginPage />} />
            <Route path="/onboarding/renter" element={<OnboardingRenter />} />
            <Route path="/onboarding/landlord" element={<OnboardingLandlord />} />
            <Route path="/listing/private/:token" element={<PrivateListingView />} />
            <Route path="/join" element={<JoinRedirect />} />
          </Routes>
        </div>
        </AuthGateProvider>
      </BrowserRouter>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
